import { useEffect, useState } from 'react';
import { isTextEntry, keyboardOpen } from '../lib/layout';

/** The visible height: the visual viewport shrinks under the keyboard on iOS and Android alike. */
function viewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

/**
 * True while the on-screen keyboard is open over a focused text field — the
 * bottom menu and the step's bottom button step aside then. Focus alone is not
 * the signal: Android's Back hides the keyboard and leaves the field focused.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // The tallest viewport seen at the current width is "no keyboard"; a turn of the phone starts over.
    let full = viewportHeight();
    let width = window.innerWidth;
    let frame = 0;
    let settle = 0;
    const check = () => {
      const height = viewportHeight();
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        full = height;
      }
      full = Math.max(full, height);
      setOpen(keyboardOpen(isTextEntry(document.activeElement), height, full));
    };
    // On focusout the next element is not focused yet, and the keyboard animates: look again a frame later.
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(check);
    };
    // A resize can report a height mid-animation: look once more when the keyboard has settled.
    const resized = () => {
      check();
      window.clearTimeout(settle);
      settle = window.setTimeout(check, 300);
    };
    const viewport = window.visualViewport;
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', later);
    viewport?.addEventListener('resize', resized);
    window.addEventListener('resize', resized);
    check();
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      document.removeEventListener('focusin', check);
      document.removeEventListener('focusout', later);
      viewport?.removeEventListener('resize', resized);
      window.removeEventListener('resize', resized);
    };
  }, []);

  return open;
}
